// Component for CloudSystem Grid
import { useEffect, useState } from 'react';

import { theme } from '../../theme/theme';
import { Button, Grid, Paper, Typography } from '@mui/material';

import CloudSystemCard from './CloudSystemCard';
import CloudDetailsSidebar from '../cloudsystem/CloudDetailsSideBar';
import ConfirmationDialog from './ConfirmationDialog';

import {  
  usePostBatchSoftwareUpdateCmdQuery, 
  usePostBatchContentUpdateCmdQuery } from "./apiDashboard";

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface FBOSmmariesProps {
//   summaries: CloudSystemSummary[]
//   cloudType: 'fbo' | 'cloudport' | 'uncommissioned'
// }

const CloudSystemGrid = (props) => {

  const [selectedSystemSerialNumber, setSelectedSystemSerialNumber] = useState(null); // CloudSummary's SerialNumber (string) or null

  const [serialsForBatchSoftwareUpdate, setSerialsForBatchSoftwareUpdate] = useState(null);
  const [needsBatchSoftwareUpdate, setNeedsBatchSoftwareUpdate] = useState(false);
  const [serialsForBatchContentUpdate, setSerialsForBatchContentUpdate] = useState(null);
  const [needsBatchContentUpdate, setNeedsBatchContentUpdate] = useState(false);

  const [batchConfirmSoftwareOpen, setBatchConfirmSoftwareOpen] = useState(false)
  const [batchConfirmContentOpen, setBatchConfirmContentOpen] = useState(false)

  const {
    data: didBatchSoftwareUpdate,
    isError: isBatchSoftwareUpdateError,
    error: batchSoftwareUpdateError,
  } = usePostBatchSoftwareUpdateCmdQuery(serialsForBatchSoftwareUpdate, { refetchOnMountOrArgChange: true, skip: !needsBatchSoftwareUpdate });

  const {
    data: didBatchContentUpdate,
    isError: isBatchContentUpdateError,
    error: batchContentUpdateError,
  } = usePostBatchContentUpdateCmdQuery(serialsForBatchContentUpdate, { refetchOnMountOrArgChange: true, skip: !needsBatchContentUpdate });

  useEffect(() => {
    if (isBatchSoftwareUpdateError) {
      console.error(`Error sending Batch Software Update command: ${batchSoftwareUpdateError}`);
    }
  }, [isBatchSoftwareUpdateError, batchSoftwareUpdateError]);

  useEffect(() => {
    if (isBatchContentUpdateError) {
      console.error(`Error sending Batch Content Update command: ${batchContentUpdateError}`);
    }
  }, [isBatchContentUpdateError, batchContentUpdateError]);

  // NOTE: Don't need to take any action when Batch Updates are successful

  const handleCloudSystemCardClicked = (serialNumber) => {
    setSelectedSystemSerialNumber(serialNumber);
  };

  const handleClose = () => {
    // Clear the selection in order to close the sidebar
    setSelectedSystemSerialNumber(null);
  };

  const handleBatchUpdateSoftwareConfirmed = () => {
    console.info("Triggering batch software update")
    // Initiate Batch update action for all shown cloud systems
    const serialsToUpdate = props.summaries?.map(s => s.serialNumber) ?? [];
    var forBatchSoftwareUpdate = {
      serialNumbers: serialsToUpdate,
      trigger: serialsForBatchSoftwareUpdate?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
    };

    setSerialsForBatchSoftwareUpdate(forBatchSoftwareUpdate);
    setNeedsBatchSoftwareUpdate(true);
  };

  const handleBatchUpdateContentConfirmed = () => {
    console.info("Triggering batch content update")
    // Initiate Batch update action for all shown cloud systems
    const serialsToUpdate = props.summaries?.map(s => s.serialNumber) ?? [];
    var forBatchContentUpdate = {
      serialNumbers: serialsToUpdate,
      trigger: serialsForBatchContentUpdate?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
    };

    setSerialsForBatchContentUpdate(forBatchContentUpdate);
    setNeedsBatchContentUpdate(true);
  };

  const onNeedsGet = () => {
    // Tell parent to Get summaries
    if (props.onNeedsGet) { 
      props.onNeedsGet()
    }
  };

  return (
    <>
        <Paper
            elevation={0}
            sx={{
                p: 2,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#112334',
                borderRadius: 0,
            }}
        >
            {props.filterApplied && (
              <Grid container direction='row' sx={{ mb: 2 }}>
                <Grid item>
                  <Typography sx={{color: theme.palette.primary.contrastText, fontWeight: 700, fontSize: '1.2rem'}}>Filtered count: {props.summaries?.length}</Typography>
                </Grid>
                <Grid item sx={{ml: 'auto'}}>
                  {props.filterTypes?.includes('Software Out Of Date') && (
                    <Button onClick={() => { setBatchConfirmSoftwareOpen(true) }} sx={{ color: theme.palette.tertiary.contrastText, backgroundColor: theme.palette.tertiary.main }}>
                      Batch Software Update
                    </Button>
                  )}
                  {props.filterTypes?.includes('Content Out Of Date') && (
                    <Button onClick={() => { setBatchConfirmContentOpen(true) }} sx={{ color: theme.palette.tertiary.contrastText, backgroundColor: theme.palette.tertiary.main, ml: 2 }}>
                      Batch Content Update
                    </Button>
                  )}
                </Grid>
              </Grid>
            )}
            <Grid container spacing={3}>
                {props.summaries && props.summaries.length > 0 ?
                  props.summaries.map((x, index) => 
                    <Grid key={index} item xs="auto">
                      <CloudSystemCard
                          cloudSystem={x}
                          cloudType={props.cloudType}
                          onClick={handleCloudSystemCardClicked}
                      />
                    </Grid>) :
                  (<Grid item sx={{top: 100}}>
                    <Typography sx={{ color: theme.palette.primary.contrastText }}>
                      NO SUMMARIES...
                    </Typography>
                  </Grid>)
                }
            </Grid>
            <CloudDetailsSidebar
                key="cloud-details-sidebar"
                cloudSystemSerial={selectedSystemSerialNumber}
                onClose={handleClose}
                onNeedsGet={onNeedsGet}
            />
            <ConfirmationDialog
              open={batchConfirmSoftwareOpen}
              onClose={() => { setBatchConfirmSoftwareOpen(false) }}
              onConfirmed={() => { handleBatchUpdateSoftwareConfirmed() }}
              title="Confirm Software Update"
              message={`You are about to trigger a software update for ${props.summaries?.length ?? 0} cloud systems`}
            />
            <ConfirmationDialog
              open={batchConfirmContentOpen}
              onClose={() => { setBatchConfirmContentOpen(false) }}
              onConfirmed={() => { handleBatchUpdateContentConfirmed() }}
              title="Confirm Content Update"
              message={`You are about to trigger a content update for ${props.summaries?.length ?? 0} cloud systems`}
            />
        </Paper>
    </>
  );
};

export default CloudSystemGrid;
